<script setup lang="ts">
import type { AlgoliaDropdownFiltersProps } from './AlgoliaDropdownFilters.props'

const { dispatchActionFilterlistingEvent } = useGAActionEvents()

withDefaults(defineProps<AlgoliaDropdownFiltersProps>(), {
  showMore: true,
})

const isNoRefinement = ref(false)
const mainDiv = ref(null)
const { hideFilter } = await useTemplatePlp()
const appConfig = useAppConfig()
const algoliaIndex = appConfig.currentAlgoliaIndex
const { getInstance } = useInstantSearch()

const preSelectedFilters = ref<CurrentFilter[]>([])

const currentRefinements = computed(() => {
  const instance = getInstance().value
  if (!instance) return []
  return instance?.renderState?.[algoliaIndex]?.currentRefinements?.items ?? []
})

const disjunctiveFacetsRefinements = computed(() => {
  const instance = getInstance().value
  return instance?.helper?.state.disjunctiveFacetsRefinements ?? {}
})

const lastAddedFilter = ref<{ attribute: string; value: string } | null>(null)

const simplifiedFilters = computed(() =>
  AlgoliaSimplifyFilters(currentRefinements.value)
)

watch(
  disjunctiveFacetsRefinements,
  (newFilters, oldFilters) => {
    let filterAdded = false

    for (const [attribute, values] of Object.entries(newFilters)) {
      const oldValues = oldFilters[attribute] || []
      const addedValues = values.filter(value => !oldValues.includes(value))

      if (addedValues.length > 0) {
        const newValue = addedValues[addedValues.length - 1]
        lastAddedFilter.value = {
          attribute,
          value: newValue,
        }
        filterAdded = true
        dispatchActionFilterlistingEvent(attribute, newValue)

        break
      }
    }

    if (!filterAdded) {
      // Check if any attribute was completely removed
      for (const [attribute] of Object.entries(oldFilters)) {
        if (!newFilters[attribute] || newFilters[attribute].length === 0) {
          lastAddedFilter.value = null

          break
        }
      }
    }
  },
  { deep: true }
)

onMounted(() => {
  isNoRefinement.value = hideFilter(mainDiv.value)
  preSelectedFilters.value = [...simplifiedFilters.value]
})

const numbersOfRefinements = (number: Number) => {
  if (number) {
    return `(${number.toString()}) `
  }
  return ``
}

const transformLabel = (label: string) => {
  const splitStr = label.split('>')
  if (splitStr.length > 1) {
    return splitStr[1].trim()
  } else {
    return label
  }
}

const refineAndOpenFilters = (
  refine: (value: string) => void,
  value: string
) => {
  refine(value)

  const subcategoriesAccordion = document.getElementById(
    'algolia-subcategory-filters'
  )
  const isSubcategoriesAccordionOpen =
    subcategoriesAccordion?.getAttribute('aria-expanded')

  if (isSubcategoriesAccordionOpen === 'false') subcategoriesAccordion?.click()

  setTimeout(() => {
    const subcategoryAccordion = document.getElementById(
      `algolia-subcategory-filters-${value}`
    )
    const isSubcategoryAccordionOpen =
      subcategoryAccordion?.getAttribute('aria-expanded')

    if (isSubcategoryAccordionOpen === 'false') subcategoryAccordion?.click()
  }, 800)
}
</script>

<template>
  <div
    v-if="!isNoRefinement"
    :id="id"
    ref="mainDiv"
    :class="{ 'border-primitives-grey-100': border }"
  >
    <AisRefinementList :attribute="attribute">
      <template
        #default="{
          items,
          refine,
          canToggleShowMore,
          toggleShowMore,
          isShowingMore,
        }"
      >
        <div
          :class="{ 'border-primitives-grey-100 border-t': items.length > 0 }"
        >
          <UIDropdown
            click-id="algolia-category-filters"
            :default-open="
              !!items.find(obj => obj.isRefined === true) || defaultOpen
            "
            :title="title"
            :right-text="
              numbersOfRefinements(
                attribute === 'microCat'
                  ? (
                      items[0] as unknown as RefinementGroup
                    )?.refinementsList?.filter(obj => obj.isRefined === true)
                      ?.length
                  : items?.filter(obj => obj.isRefined === true)?.length
              )
            "
          >
            <div class="mb-6 flex flex-col gap-6 font-light">
              <div>
                <ul class="flex flex-col gap-4">
                  <li
                    v-for="item in attribute === 'microCat'
                      ? (items[0] as unknown as RefinementGroup).refinementsList
                      : items"
                    :key="`${item.value}_${attribute}`"
                    class="flex gap-1"
                  >
                    <AtomsCheckbox
                      v-model="item.isRefined"
                      @refine="refineAndOpenFilters(refine, item.value)"
                    >
                      <template #label>
                        <span>
                          {{ transformLabel(item.label) }}
                        </span>
                        <span
                          v-if="item?.count"
                          class="text-light-7 text-primitives-grey-200 mb-1 flex w-fit items-center"
                        >
                          ({{ item.count }})
                        </span>
                      </template>
                    </AtomsCheckbox>
                  </li>
                </ul>
                <AtomsCta
                  v-if="canToggleShowMore"
                  anatomy="link"
                  @click="toggleShowMore"
                >
                  <template #label>
                    {{ !isShowingMore ? 'Show more' : 'Show less' }}
                  </template>
                </AtomsCta>
              </div>
            </div>
          </UIDropdown>
        </div>
      </template>
      <template #showMoreLabel>
        <span>
          <!-- hide default show more -->
        </span>
      </template>
    </AisRefinementList>
  </div>
</template>
